import React from 'react'

import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  type ButtonProps,
  Color,
  Display,
  FlexDirection,
  Heading3,
  JustifyContent,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

export type NumberPillSelectorProps = {
  /** Visible heading for the group, e.g. "Select number of people". Also names the group. */
  legend: string
  /** The values on offer, in the order they should appear. */
  options: number[]
  /** The currently selected value. */
  value: number
  onChange: (value: number) => void
}

type PillProps = ButtonProps & { isSelected: boolean }

/**
 * One component for both states, switched by a prop. Two separate `withCitrusTheme` components
 * would be two React element types, so selecting would remount the button the visitor just
 * activated and drop keyboard focus. `omitProps` keeps `isSelected` off the `<button>`.
 *
 * The border is ColdGrey_500, not the design's ColdGrey_200, which fails the 3:1 WCAG 1.4.11
 * non-text contrast minimum against white.
 */
const Pill = withCitrusTheme<PillProps>(
  Button,
  (theme, { isSelected }) => ({
    borderRadius: '8px',
    height: '48px',
    // `minWidth: 0` lets pills shrink below their content width, so five fit 375px without
    // pushing the row wider.
    flex: '1 0 0',
    minWidth: 0,
    ...(isSelected
      ? { boxShadow: `0 0 0 2px ${theme.color(Color.Secondary_600)} inset` }
      : {
          backgroundColor: theme.color(Color.White),
          border: `1px solid ${theme.color(Color.ColdGrey_500)}`,
          color: theme.color(Color.ColdGrey_800),
        }),
  }),
  { omitProps: ['isSelected'] },
)

export const NumberPillSelector = ({
  legend,
  options,
  value,
  onChange,
}: NumberPillSelectorProps) => {
  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={4}>
      <Heading3 size={5} color={Color.ColdGrey_800}>
        {legend}
      </Heading3>
      <Box
        role="group"
        // Named by the same text as the visible heading. An `aria-label` rather than
        // `aria-labelledby` because this React version has no `useId` to mint a stable id with.
        aria-label={legend}
        display={Display.Flex}
        wrap
        alignItems={AlignItems.Stretch}
        justifyContent={JustifyContent.FlexStart}
        gap={2}
      >
        {options.map((option) => {
          const isSelected = option === value

          return (
            <Pill
              key={option}
              isSelected={isSelected}
              aria-pressed={isSelected}
              colorVariant={isSelected ? ButtonColorVariant.Secondary : ButtonColorVariant.Tertiary}
              // Re-selecting the current value still reports the change. Suppressing it would
              // make the experiment's arms non-comparable: the control widget's button has no
              // such guard, so it tracks a redundant click and this would not.
              onClick={() => onChange(option)}
            >
              {String(option)}
            </Pill>
          )
        })}
      </Box>
    </Box>
  )
}
